import { motion } from "framer-motion";
import { TreePine, Home, HandHeart } from "lucide-react";

const MISSION_IMG =
  "https://images.unsplash.com/photo-1773401302001-cd608ccd2833?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxzZWVkbGluZyUyMGdyb3dpbmclMjBmb3Jlc3R8ZW58MHx8fHwxNzgyODYxNjE4fDA&ixlib=rb-4.1.0&q=85";

const pillars = [
  {
    icon: TreePine,
    title: "Reforest",
    body: "A fixed share of every transaction flows into a transparent on-chain treasury that funds native tree planting in burn-scarred regions.",
  },
  {
    icon: Home,
    title: "Rebuild",
    body: "Grants are deployed to local builders and families to reconstruct homes, schools and shared spaces lost to wildfire.",
  },
  {
    icon: HandHeart,
    title: "Empower",
    body: "Token holders vote on which recovery projects get funded next — putting the community in charge of the comeback.",
  },
];

export default function Mission() {
  return (
    <section id="mission" className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">
            The Mission
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
            Recovery, written into the protocol.
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Wildfires take everything in hours. Rebuilding takes years — and money that rarely
            arrives fast enough. AfterBurn turns crypto momentum into real, traceable funding for
            the long road back.
          </p>

          <div className="mt-10 space-y-6">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex gap-5"
                data-testid={`pillar-${p.title.toLowerCase()}`}
              >
                <div className="shrink-0 w-12 h-12 border border-ember/40 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-ember" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl">{p.title}</h3>
                  <p className="text-muted-foreground mt-1 leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="absolute -inset-3 bg-ember/10 blur-2xl" />
          <img
            src={MISSION_IMG}
            alt="A green sprout emerging from the forest floor"
            className="relative w-full h-[520px] object-cover border border-white/10"
          />
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-background to-transparent h-1/3" />
          <div className="absolute bottom-6 left-6 font-mono text-sm text-foreground/90">
            Year one → 10,000,000 trees
          </div>
        </motion.div>
      </div>
    </section>
  );
}
